/*
 * Brand size-guide behaviour: product-type and audience tabs, and the How to
 * Measure popup.
 *
 * Everything is delegated from the document, so a component that arrives after
 * DOMContentLoaded works exactly like one that was in the first paint. Prestige
 * needs that: it renders the variant picker twice on a product page, keeps the
 * second copy inside <template id="quick-buy-content">, and clones it into the
 * document when the shopper opens quick buy.
 *
 * Because nothing is bound per element, the same component can be encountered
 * any number of times without stacking handlers, and two instances on one page
 * never touch each other's tabs, panels or popup. Each instance carries its own
 * id namespace, supplied by the caller, so every aria-controls and
 * aria-labelledby resolves to exactly one element.
 *
 * The markup is already correct and readable without this file.
 */
(function () {
  'use strict';

  /* Loaded once per page, but a theme can include an asset twice. */
  if (window.sizingGuideBrandReady) return;
  window.sizingGuideBrandReady = true;

  var KEYS = {
    left: 'ArrowLeft',
    right: 'ArrowRight',
    up: 'ArrowUp',
    down: 'ArrowDown',
    home: 'Home',
    end: 'End'
  };

  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function closest(node, selector) {
    if (!node || typeof node.closest !== 'function') return null;
    return node.closest(selector);
  }

  /* --------------------------------------------------------------- tabs */

  function tabsIn(tablist) {
    return Array.prototype.filter.call(tablist.children, function (child) {
      return child.getAttribute && child.getAttribute('role') === 'tab';
    });
  }

  /*
    The panel a tab controls is looked up inside the tab's own component, not
    the document, so a cloned instance switches its own panels even while an
    identically shaped instance is on the page.
  */
  function panelFor(root, tab) {
    var id = tab.getAttribute('aria-controls');
    if (!id) return null;
    return root.querySelector('[id="' + id + '"]') || document.getElementById(id);
  }

  function select(root, tabs, index, focus) {
    for (var i = 0; i < tabs.length; i += 1) {
      var tab = tabs[i];
      var panel = panelFor(root, tab);
      var active = i === index;
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.setAttribute('tabindex', active ? '0' : '-1');
      if (panel) {
        if (active) panel.removeAttribute('hidden');
        else panel.setAttribute('hidden', '');
      }
    }
    if (focus) tabs[index].focus();
  }

  function activate(tab, focus) {
    var tablist = closest(tab, '[role="tablist"]');
    var root = closest(tab, '[data-sizing-guide]');
    if (!tablist || !root) return null;
    var tabs = tabsIn(tablist);
    var index = tabs.indexOf(tab);
    if (index === -1 || tabs.length < 2) return null;
    root.setAttribute('data-sizing-guide-ready', '');
    select(root, tabs, index, focus);
    return { tabs: tabs, index: index, root: root };
  }

  /* --------------------------------------------- contextual opening */

  /*
    Opening the guide where the product actually is.

    The destination arrives on the trigger as three normalized values — category,
    audience and an optional section — put there by Liquid from a manifest keyed
    by Shopify product id. Nothing is inferred here from a title, a tag, a URL or
    anything the customer did earlier: this function is handed a destination or
    it does nothing at all.

    Matching is by id SUFFIX rather than by a full id. Every instance carries its
    own uid namespace, and the uid is opaque and may itself contain hyphens, so
    the only stable part of `sizing-guide-capezio-<uid>-dancewear-girls-tab` is
    its tail. The tail is always fully qualified — category, then audience, then
    section — so a shorter block id can never match a longer one by accident.
  */

  function endsWithSegment(element, suffix) {
    var id = element.getAttribute('id');
    return typeof id === 'string' && id.length > suffix.length && id.slice(-suffix.length) === suffix;
  }

  function findInTablist(tablist, suffix) {
    if (!tablist) return null;
    var tabs = tabsIn(tablist);
    for (var i = 0; i < tabs.length; i += 1) {
      if (endsWithSegment(tabs[i], suffix)) return tabs[i];
    }
    return null;
  }

  /*
    Select without focusing. The customer's focus belongs to the modal, which
    manages it itself; stealing it here would move the caret out of the dialog
    the theme just opened.
  */
  function selectTab(root, tab) {
    var tablist = closest(tab, '[role="tablist"]');
    if (!tablist) return false;
    var tabs = tabsIn(tablist);
    var index = tabs.indexOf(tab);
    if (index === -1) return false;
    select(root, tabs, index, false);
    return true;
  }

  /*
    The scrollable box the guide sits in — the modal's own, never the page's.
    The walk stops at <body>, and neither <body> nor <html> is ever eligible, so
    the product page underneath cannot be moved by anything here. In practice the
    modal's own scroller is the first one found; an intermediate scrolling
    container between the guide and the body would be used instead, which is the
    correct answer for a guide rendered inside one.
  */
  function scrollerFor(element) {
    var node = element.parentNode;
    while (node && node.nodeType === 1 && node !== document.body) {
      var style = window.getComputedStyle(node);
      var scrolls = /(auto|scroll|overlay)/.test(style.overflowY);
      if (scrolls && node.scrollHeight > node.clientHeight + 1) return node;
      node = node.parentNode;
    }
    return null;
  }

  function bringToTop(block) {
    var scroller = scrollerFor(block);
    if (!scroller) return;
    var offset = block.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
    scroller.scrollTop = scroller.scrollTop + offset;
  }

  function openAt(root, destination) {
    if (!root || !destination || !destination.category || !destination.audience) return false;

    /*
      RESOLVE EVERYTHING FIRST, THEN APPLY. Nothing below this comment touches a
      tab until the whole destination — category AND audience — has been found.

      The first version selected the category as soon as it matched and only
      then looked for the audience. A destination naming a category the guide
      HAS and an audience it does NOT therefore returned false having already
      moved the category: the guide was left on a tab nobody asked for, with no
      data-sizing-guide-opened-at to say the destination had failed. That is the
      opposite of the fallback this false return exists to provide, and it is
      reachable exactly when the manifest and the guide have drifted apart —
      which is the one case the fallback is for.
    */
    var categoryTablist = root.querySelector('.sizing-guide__tablist--category');
    var categoryTab = findInTablist(categoryTablist, '-' + destination.category + '-tab');

    /*
      A guide with one category renders no category tablist, which is not a
      failure: the single panel is already the right one. What IS a failure is
      being asked for a category this guide does not have.
    */
    var categoryPanel = null;
    if (categoryTab) categoryPanel = panelFor(root, categoryTab);
    else if (categoryTablist) return false;
    else categoryPanel = root.querySelector('.sizing-guide__panel--category');
    if (!categoryPanel) return false;

    var audienceSuffix = '-' + destination.category + '-' + destination.audience;
    var audienceTab = findInTablist(
      categoryPanel.querySelector('.sizing-guide__tablist--audience'),
      audienceSuffix + '-tab'
    );
    var audiencePanel = null;
    if (audienceTab) {
      audiencePanel = panelFor(root, audienceTab);
    } else {
      /* One audience: the panel exists and is already revealed. */
      var panels = categoryPanel.querySelectorAll('.sizing-guide__panel--audience');
      for (var i = 0; i < panels.length; i += 1) {
        if (endsWithSegment(panels[i], audienceSuffix + '-panel')) audiencePanel = panels[i];
      }
    }
    if (!audiencePanel) return false;

    /* Resolved. From here the guide is changed, and every step succeeds: each
       tab was found inside its own tablist, which is all selectTab requires. */
    if (categoryTab && !selectTab(root, categoryTab)) return false;
    if (audienceTab && !selectTab(root, audienceTab)) return false;

    root.setAttribute('data-sizing-guide-ready', '');
    root.setAttribute(
      'data-sizing-guide-opened-at',
      destination.category + '/' + destination.audience + (destination.section ? '/' + destination.section : '')
    );

    /*
      Bring the relevant section to the top of the modal — but only when it is
      not already the first thing in the panel.

      For most audiences the requested section IS the first block, and scrolling
      to it would push the category and audience tabs off the top of the modal
      for no gain: the customer would arrive at the right chart with the
      navigation out of sight and nothing to say it was there. Resetting to the
      top instead puts the same chart at the beginning of the visible content
      AND leaves every other tab on screen, which is the whole point of letting
      people switch afterwards.

      When the section is further down — a second or third chart in the same
      audience — it is scrolled to, because otherwise the customer would open on
      a chart that is not theirs.
    */
    var scroller = audiencePanel && scrollerFor(audiencePanel);
    if (scroller) scroller.scrollTop = 0;

    if (destination.section && audiencePanel) {
      var heading = null;
      var headings = audiencePanel.querySelectorAll('[id]');
      var suffix = audienceSuffix + '-' + destination.section + '-heading';
      for (var h = 0; h < headings.length; h += 1) {
        if (endsWithSegment(headings[h], suffix)) heading = headings[h];
      }
      var block = heading && closest(heading, '.sizing-guide__block');
      if (block && block !== audiencePanel.querySelector('.sizing-guide__block')) bringToTop(block);
    }
    return true;
  }

  function destinationOf(trigger) {
    var category = trigger.getAttribute('data-sizing-guide-open-category');
    var audience = trigger.getAttribute('data-sizing-guide-open-audience');
    if (!category || !audience) return null;
    return {
      category: category,
      audience: audience,
      section: trigger.getAttribute('data-sizing-guide-open-section') || ''
    };
  }

  /*
    The trigger opens the theme's own dialog, and the guide inside it may not be
    laid out — or, in quick buy, may not yet be in the document — on the click
    itself.

    WHAT IS BEING WAITED FOR is the guide ARRIVING, not the destination
    succeeding. The first version retried while `openAt` returned false, which
    conflated the two: a destination this guide genuinely cannot satisfy spun
    the loop out and, worse, a guide attached a few frames late was abandoned
    because the budget had already been spent on a guide that was not there yet.
    Now the loop ends the moment the guide exists — applying the destination
    once, whatever the answer — and only keeps waiting while it does not.

    Half a second of animation frames is far longer than a clone needs and costs
    nothing once the guide is found, because the loop stops on the first frame
    that finds it.
  */
  var WAIT_FRAMES = 30;

  function applyWhenReady(trigger) {
    var destination = destinationOf(trigger);
    if (!destination) return;
    var id = trigger.getAttribute('aria-controls');
    if (!id) return;
    var attempts = 0;
    var tick = function () {
      var modal = document.getElementById(id);
      var root = modal && modal.querySelector('[data-sizing-guide]');
      if (root) {
        openAt(root, destination);
        return;
      }
      attempts += 1;
      if (attempts < WAIT_FRAMES) window.requestAnimationFrame(tick);
    };
    window.requestAnimationFrame(tick);
  }

  /*
    Exposed so the regression suite can drive a destination directly against a
    rendered guide, without a modal or a theme around it.
  */
  window.sizingGuideOpenAt = openAt;

  /* ------------------------------------------------- How to Measure popup */

  /* Only one modal can be open, so one record describes the open one. */
  var open = null;

  function focusableIn(element) {
    return Array.prototype.filter.call(element.querySelectorAll(FOCUSABLE), function (candidate) {
      return candidate.offsetWidth > 0 || candidate.offsetHeight > 0 || candidate === document.activeElement;
    });
  }

  function openersFor(overlay) {
    return document.querySelectorAll('[data-sizing-guide-measure-open][aria-controls="' + overlay.id + '"]');
  }

  function setExpanded(overlay, value) {
    var openers = openersFor(overlay);
    for (var i = 0; i < openers.length; i += 1) openers[i].setAttribute('aria-expanded', value);
  }

  function openOverlay(button) {
    if (open) return;
    var overlay = document.getElementById(button.getAttribute('aria-controls'));
    if (!overlay) return;
    var panel = overlay.querySelector('[role="dialog"]');
    if (!panel) return;

    /*
      On the product page the guide is already inside the theme's modal. Moving
      the overlay to a direct child of <body> for as long as it is open keeps it
      out of that dialog — no dialog is ever nested inside another — and puts it
      above the theme's own stacking contexts. Where it came from is remembered
      so it can go home on close and travel with its component if the theme
      throws that component away.
    */
    var home = null;
    if (overlay.parentNode !== document.body) {
      home = { parent: overlay.parentNode, next: overlay.nextSibling };
      document.body.appendChild(overlay);
    }

    open = {
      overlay: overlay,
      panel: panel,
      trigger: button,
      home: home,
      scrollLock: document.body.style.overflow
    };

    overlay.removeAttribute('hidden');
    setExpanded(overlay, 'true');
    document.body.style.overflow = 'hidden';
    var first = focusableIn(panel)[0];
    (first || panel).focus();
  }

  function closeOverlay() {
    if (!open) return;
    var state = open;
    open = null;

    state.overlay.setAttribute('hidden', '');
    setExpanded(state.overlay, 'false');
    document.body.style.overflow = state.scrollLock;

    if (state.home && state.home.parent && state.home.parent.isConnected) {
      state.home.parent.insertBefore(state.overlay, state.home.next);
    }
    if (state.trigger && document.contains(state.trigger)) state.trigger.focus();
  }

  function trapTab(event) {
    var stops = focusableIn(open.panel);
    if (!stops.length) {
      event.preventDefault();
      open.panel.focus();
      return;
    }
    var first = stops[0];
    var last = stops[stops.length - 1];
    var active = document.activeElement;
    if (!open.panel.contains(active)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    } else if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ---------------------------------------------------------- delegation */

  document.addEventListener('click', function (event) {
    if (closest(event.target, '[data-sizing-guide-measure-close]')) {
      closeOverlay();
      return;
    }
    var opener = closest(event.target, '[data-sizing-guide-measure-open]');
    if (opener) {
      openOverlay(opener);
      return;
    }
    var trigger = closest(event.target, '[data-sizing-guide-open-category]');
    if (trigger) {
      applyWhenReady(trigger);
      return;
    }
    var tab = closest(event.target, '.sizing-guide__tab[role="tab"]');
    if (tab) activate(tab, false);
  });

  /*
    The trigger's own inline handler calls preventDefault for Space, which stops
    the browser generating the click that would otherwise carry the destination.
    Keyboard opening has to reach the same place as mouse opening.
  */
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    var trigger = closest(event.target, '[data-sizing-guide-open-category]');
    if (trigger) applyWhenReady(trigger);
  });

  document.addEventListener('keydown', function (event) {
    if (open) {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeOverlay();
        return;
      }
      if (event.key === 'Tab') {
        trapTab(event);
        return;
      }
    }

    var tab = closest(event.target, '.sizing-guide__tab[role="tab"]');
    if (!tab) return;
    var tablist = closest(tab, '[role="tablist"]');
    if (!tablist) return;
    var tabs = tabsIn(tablist);
    var index = tabs.indexOf(tab);
    if (index === -1 || tabs.length < 2) return;

    var next = null;
    if (event.key === KEYS.right || event.key === KEYS.down) next = (index + 1) % tabs.length;
    else if (event.key === KEYS.left || event.key === KEYS.up) next = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === KEYS.home) next = 0;
    else if (event.key === KEYS.end) next = tabs.length - 1;
    if (next === null) return;

    event.preventDefault();
    activate(tabs[next], true);
  });

  /*
    The server-rendered markup already carries the correct aria-selected,
    tabindex and hidden state, so nothing has to be initialised for a component
    to be usable. This only marks what is present for styling and for tests.
  */
  function mark() {
    var roots = document.querySelectorAll('[data-sizing-guide]');
    for (var i = 0; i < roots.length; i += 1) roots[i].setAttribute('data-sizing-guide-ready', '');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mark, { once: true });
  } else {
    mark();
  }
})();
